import { UsageIntent } from '@/Enums/FirstPartyData/UsageIntent.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import InfoIcon from '@/components/Icons/InfoIcon';
import ModalBody from '@/components/Modal/common/ModalBody';
import { useAppDispatch } from '@/store/hooks';
import { getFirstPartyData, uploadFirstPartyData } from '@/store/slices/firstPartyDataSlice';
import { Button, FormHelperText, Modal } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import FileDropZone from '../Components/FileDropZone';
import FileInfo from '../Components/FileInfo';

const UploadModal = ({ open, setLoading, onModalClose }) => {
  const dispatch = useAppDispatch();
  const formMethods = useForm({
    defaultValues: {
      name: '',
      advertiser: null,
      file: null,
      dateRange: [null, null],
      usageIntent: UsageIntent.Attribution,
    },
  });

  const [errorDateText, setErrorDateText] = useState(null);
  const [errorFileText, setErrorFileText] = useState(null);
  const { handleSubmit, watch, reset, setValue } = formMethods;

  const onSubmit = async data => {
    const { dateRange, advertiser, ...dataToSend } = data;
    if (!dateRange[0] || !data.file) {
      if (dataToSend.usageIntent === UsageIntent.Attribution) {
        if (!dateRange[0]) {
          setErrorDateText('* Date range is required');
        }
        if (!data.file) {
          setErrorFileText('* Upload file is required');
        }
        return;
      }

      if (dataToSend.usageIntent === UsageIntent.Retargeting) {
        if (!dateRange[0]) {
          setErrorDateText(null);
          dataToSend.startDate = null;
          dataToSend.endDate = null;
        }
        if (!data.file) {
          setErrorFileText('* Upload file is required');
          return;
        }
      }
    } else {
      dataToSend.startDate = dateRange[0].format(DateFormat.Iso8601);
      dataToSend.endDate = dateRange[1].format(DateFormat.Iso8601);
    }

    dataToSend.advertiserId = advertiser.value;

    setLoading(true);

    try {
      await dispatch(uploadFirstPartyData(dataToSend)).unwrap();
      setLoading(false);

      onModalClose();
      toast.success('File uploaded successfully');
      reset();
      dispatch(getFirstPartyData());
    } catch (error) {
      setLoading(false);
      if (error?.response?.data?.message?.includes('Headers do not match the required format')) {
        toast.error('Please make sure your column order aligns with the downloaded template');
      } else if (error?.response?.data?.message?.includes('email and/or phone number')) {
        toast.error('Please upload file with email and / or phone number included.');
      } else if (error?.response?.data?.message?.includes('This file was already uploaded.')) {
        toast.error('This file was already uploaded.');
      } else if (
        error?.response?.data?.message?.includes('This file is already uploaded and currently being processed.')
      ) {
        toast.error('This file is already uploaded and currently being processed.');
      } else {
        toast.error('Something went wrong');
        console.error(error);
      }
    }
  };

  const setUploadedFile = useCallback(
    file => {
      setErrorFileText(null);
      setValue('file', file);
      setValue('name', file.name);
    },
    [setErrorFileText, setValue],
  );

  const handleClose = useCallback(() => {
    onModalClose();
    reset();
    setErrorDateText(null);
    setErrorFileText(null);
  }, [onModalClose, reset]);

  return (
    <Modal open={open} onClose={onModalClose} aria-labelledby='modal-title'>
      <ModalBody>
        <h2 className='font-semibold text-[24px]'>Upload File</h2>
        <hr className='-mx-[30px] my-2' />
        <FormProvider {...formMethods}>
          <FileInfo errorDateText={errorDateText} setErrorDateText={setErrorDateText} />
          <form className='mt-2' onSubmit={handleSubmit(onSubmit)}>
            <FileDropZone setUploadedFile={setUploadedFile} watch={watch} />
            {!!errorFileText && (
              <FormHelperText error id='file-error'>
                {errorFileText}
              </FormHelperText>
            )}
            <div className='flex gap-1'>
              <InfoIcon />
              <span className='text-[#8E8F94] text-xs'>
                Please only use the Download File provided here to upload your data. Please fill in as many columns as
                possible, for each row that you submit in the file. In the file name, do not include periods or spaces.
                At the very least please include email and / or phone number when submitting. Please review the sample
                file Row 2 to see which fields are required and which are optional. When pasting data, please remove the
                contents in Row 2 and paste your data starting with Row 2.
              </span>
            </div>
            <div className='mt-2 flex justify-end gap-[12px]'>
              <Button color='inherit' variant='outlined' onClick={handleClose}>
                Close
              </Button>
              <Button color='primary' variant='contained' type='submit'>
                Upload
              </Button>
            </div>
          </form>
        </FormProvider>
      </ModalBody>
    </Modal>
  );
};

UploadModal.propTypes = {
  open: PropTypes.bool,
  setLoading: PropTypes.func,
  onModalClose: PropTypes.func,
};

export default UploadModal;
